import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PersonRef,
  DisplayedRequestStatus,
  DuplicateIgnoredInput,
  DuplicateSearchInput,
  DuplicateSearchResponse,
  ProductCard,
  ReleaseNoteDetail,
  ReleaseNoteSummary,
  RequestDetail,
  RequestListResponse,
  RequestSubmissionInput,
  RequestSummary,
  UpcomingVersion,
} from '@product-portal/shared';
import { apiFetch } from '@/lib/api';

/**
 * Data of screens 01 to 05. Every rule (visibility, internal notes, PO-only
 * fields) is applied by the API; these hooks only fetch and cache.
 */

export const requestKeys = {
  all: ['requests'] as const,
  list: (params: object) => ['requests', 'list', params] as const,
  summary: ['requests', 'summary'] as const,
  detail: (id: string) => ['requests', 'detail', id] as const,
  duplicates: (input: DuplicateSearchInput | null) => ['requests', 'duplicates', input] as const,
};

export function useRequestList(params: { scope: 'mine' | 'team'; status?: DisplayedRequestStatus; page: number; pageSize: number }) {
  const search = new URLSearchParams({ scope: params.scope, page: String(params.page), pageSize: String(params.pageSize) });
  if (params.status) search.set('status', params.status);
  return useQuery({
    queryKey: requestKeys.list(params),
    queryFn: () => apiFetch<RequestListResponse>(`/api/requests?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function useRequestSummary() {
  return useQuery({ queryKey: requestKeys.summary, queryFn: () => apiFetch<RequestSummary>('/api/requests/summary') });
}

export function useRequest(id: string) {
  return useQuery({ queryKey: requestKeys.detail(id), queryFn: () => apiFetch<RequestDetail>(`/api/requests/${id}`), retry: false });
}

/** POST: the typed description and client fields never go into a URL. */
export function searchDuplicates(input: DuplicateSearchInput): Promise<DuplicateSearchResponse> {
  return apiFetch<DuplicateSearchResponse>('/api/requests/duplicates', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** Side panel of 02 and 15 (ADR-009). `null`: nothing to search yet. */
export function useDuplicates(input: DuplicateSearchInput | null) {
  return useQuery({
    queryKey: requestKeys.duplicates(input),
    queryFn: () => searchDuplicates(input!),
    enabled: input !== null,
    placeholderData: keepPreviousData,
  });
}

/** « Revenir au formulaire » / « Annuler »: the suggestions shown are journaled as ignored. Never blocks. */
export function journalIgnoredSuggestions(input: DuplicateIgnoredInput): void {
  void apiFetch('/api/requests/duplicates/ignored', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).catch(() => undefined);
}

export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => apiFetch<{ items: ProductCard[] }>('/api/products').then((r) => r.items),
    staleTime: 60_000,
  });
}

export function useReleaseNotes(productId?: string) {
  return useQuery({
    queryKey: ['release-notes', productId ?? 'all'],
    queryFn: () =>
      apiFetch<{ items: ReleaseNoteSummary[] }>(`/api/release-notes${productId ? `?productId=${productId}` : ''}`).then((r) => r.items),
  });
}

export function useUpcomingVersions(productId?: string) {
  return useQuery({
    queryKey: ['release-notes', 'upcoming', productId ?? 'all'],
    queryFn: () =>
      apiFetch<{ items: UpcomingVersion[] }>(`/api/release-notes/upcoming${productId ? `?productId=${productId}` : ''}`).then(
        (r) => r.items,
      ),
  });
}

export function useReleaseNote(id: string | null) {
  return useQuery({
    queryKey: ['release-notes', 'detail', id],
    queryFn: () => apiFetch<ReleaseNoteDetail>(`/api/release-notes/${id}`),
    enabled: id !== null,
  });
}

function multipartBody(data: unknown, files: File[]): FormData {
  const form = new FormData();
  form.append('data', JSON.stringify(data));
  for (const file of files) form.append('files', file, file.name);
  return form;
}

export function useSubmitRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, files }: { input: RequestSubmissionInput; files: File[] }) =>
      apiFetch<{ id: string; reference: string }>('/api/requests', { method: 'POST', body: multipartBody(input, files) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: requestKeys.all }),
  });
}

/** « C'est le même problème » / « M'abonner et ajouter mon contexte »: no request is created. */
export function useSameProblem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, place, input, files }: { candidateId: string; place: 'panel' | 'prompt'; input: RequestSubmissionInput; files: File[] }) =>
      apiFetch<{ id: string; reference: string }>(`/api/requests/${candidateId}/same-problem?place=${place}`, { method: 'POST', body: multipartBody(input, files) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: requestKeys.all }),
  });
}

export function useReply(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, files }: { body: string; files: File[] }) =>
      apiFetch<{ id: string }>(`/api/requests/${requestId}/messages`, { method: 'POST', body: multipartBody({ body }, files) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: requestKeys.all }),
  });
}

/** People a PO or a Référent of the request's team can subscribe (2026-10-23). */
export function useSubscriberCandidates(requestId: string, q: string) {
  return useQuery({
    queryKey: ['requests', 'subscriber-candidates', requestId, q.trim()] as const,
    queryFn: () =>
      apiFetch<{ items: (PersonRef & { teamName: string | null })[] }>(
        `/api/requests/${requestId}/subscriber-candidates?${new URLSearchParams({ q: q.trim() })}`,
      ).then((r) => r.items),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function useSubscribeOther(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => apiFetch(`/api/requests/${requestId}/subscribers/${userId}`, { method: 'PUT' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: requestKeys.all }),
  });
}

export function useSubscription(requestId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscribed: boolean) =>
      apiFetch<{ subscribed: boolean }>(`/api/requests/${requestId}/subscription`, { method: subscribed ? 'PUT' : 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: requestKeys.all }),
  });
}
